import { useEffect, useState } from 'react';
import {
  ApiError,
  NETWORK_ERROR_MESSAGE,
  decodeTextBytes,
  documentReadFailureMessage,
  fetchDocumentPreview,
  type DocumentPreview,
} from '@dataroom/shared';
import { previewKindFor } from '@/lib/viewer';

/** Fetched content: an object URL for images, the raw bytes for pdf.js, decoded
 * text, or the server-built office preview (ADR-0033). */
export type LoadedContent =
  | { kind: 'image'; url: string }
  | { kind: 'pdf'; data: ArrayBuffer }
  | { kind: 'text'; text: string }
  | { kind: 'office'; preview: DocumentPreview }
  | { kind: 'unsupported' };

export interface DocumentContentState {
  content: LoadedContent | null;
  error: string | null;
  loading: boolean;
  /** The version the server served (`X-Document-Version`, ADR-0056); null when
   * it did not say (the LOI read, an older server). */
  versionId?: string | null;
}

/** The header naming the document version a content / preview read served. */
export const DOCUMENT_VERSION_HEADER = 'X-Document-Version';

/**
 * Fetch a document for the protected viewer and classify it for rendering.
 *
 *   - With a `previewUrl` (an office file — xlsx, csv, docx, … — whose bytes the
 *     content endpoint only ever serves as an attachment), the read-only JSON
 *     preview is fetched instead of the bytes (ADR-0033).
 *   - Otherwise the bytes come from the same-origin, credentialed content
 *     endpoint (BINARY, so raw `fetch` rather than the JSON api client): pdf.js
 *     bytes, an image object URL, plain text, or "unsupported". Bytes that turn
 *     out to be an image no browser renders (TIFF, HEIC — a file whose name did
 *     not say so) are shown from `fallbackPreviewUrl` when there is one.
 *
 * A closed/switched document aborts the read, so stale content is never shown;
 * an image object URL is revoked on cleanup. `notFoundMessage` is what a 404
 * reads as for this caller (a room document that is "no longer available", an
 * agreement that "can't be loaded").
 */
export function useDocumentContent(
  contentUrl: string,
  notFoundMessage = 'This document is no longer available to you.',
  previewUrl?: string | null,
  fallbackPreviewUrl?: string | null,
): DocumentContentState {
  const [state, setState] = useState<DocumentContentState>({
    content: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    let objectUrl: string | null = null;
    const controller = new AbortController();
    setState({ content: null, error: null, loading: true });
    // The version the last response named (the one whose content is shown).
    let versionId: string | null = null;
    const noteVersion = (res: Response) => {
      versionId = res.headers.get(DOCUMENT_VERSION_HEADER);
    };

    const loadPreview = (url: string, fallBackToBytes: boolean): Promise<void> =>
      fetchDocumentPreview(url, controller.signal, notFoundMessage, noteVersion).then((preview) => {
        if (controller.signal.aborted) return;
        if (preview.kind === 'unsupported' && preview.reason === 'type') {
          if (fallBackToBytes) return loadBytes();
          setState({ content: { kind: 'unsupported' }, error: null, loading: false });
          return;
        }
        setState({ content: { kind: 'office', preview }, error: null, loading: false, versionId });
      });

    const loadBytes = (): Promise<void> =>
      fetch(contentUrl, { credentials: 'include', signal: controller.signal }).then(async (res) => {
          noteVersion(res);
          if (res.status === 403 && (await isDownloadNotPermitted(res))) {
            // A view-only caller asked for a file type that can only be
            // downloaded (ADR-0033): nothing to show, not an error.
            if (controller.signal.aborted) return;
            setState({ content: { kind: 'unsupported' }, error: null, loading: false });
            return;
          }
          if (!res.ok) {
            // 503 = a storage read failure (temporary), not a missing document.
            throw new ApiError(res.status, documentReadFailureMessage(res.status, notFoundMessage));
          }
          const blob = await res.blob();
          const kind = previewKindFor(res.headers.get('Content-Type') ?? blob.type);
          if (kind === 'preview' && fallbackPreviewUrl) {
            return loadPreview(fallbackPreviewUrl, false);
          }
          // PDFs go to pdf.js as bytes — no object URL, nothing a plugin can open.
          const data = kind === 'pdf' ? await blob.arrayBuffer() : null;
          const text = kind === 'text' ? decodeTextBytes(await blob.arrayBuffer()) : null;
          if (controller.signal.aborted) return;
          if (kind === 'image') {
            objectUrl = URL.createObjectURL(blob);
            setState({ content: { kind, url: objectUrl }, error: null, loading: false, versionId });
          } else if (kind === 'pdf' && data) {
            setState({ content: { kind, data }, error: null, loading: false, versionId });
          } else if (kind === 'text' && text !== null) {
            setState({ content: { kind, text }, error: null, loading: false, versionId });
          } else {
            setState({ content: { kind: 'unsupported' }, error: null, loading: false });
          }
        });

    // The extension picked the preview, but the server decides the format from
    // the stored type: a PDF saved as "….docx" answers `unsupported/type` there,
    // and is then shown from its bytes like any other PDF.
    const load = previewUrl ? loadPreview(previewUrl, true) : loadBytes();

    load.catch((err: unknown) => {
      // A fetch aborted by the cleanup below is expected (close/unmount, or
      // StrictMode's double-invoke) — not an error to surface.
      if (controller.signal.aborted) return;
      // Our own status messages are ApiErrors; anything else is the fetch or
      // body read failing (a network drop) — never show its raw text.
      setState({
        content: null,
        error: err instanceof ApiError ? err.message : NETWORK_ERROR_MESSAGE,
        loading: false,
      });
    });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [contentUrl, notFoundMessage, previewUrl, fallbackPreviewUrl]);

  return state;
}

/** True when a 403 is the content route's "view in the preview only" refusal. */
async function isDownloadNotPermitted(res: Response): Promise<boolean> {
  try {
    const body = (await res.json()) as { code?: unknown };
    return body.code === 'download_not_permitted';
  } catch {
    return false;
  }
}
